import { Description, DialogTitle, Radio, RadioGroup } from "@headlessui/react";
import { useQueryClient } from "@tanstack/react-query";

import { JSX, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import Button from "@/components/buttons/Button.tsx";
import { IconRadio } from "@/components/icons";
import Input from "@/components/inputs/Input.tsx";
import "@/components/layout/StaffLayout/StaffLayout.css";

import { COLORS } from "@/config/colors.ts";
import { USER_KEYS, useChangeSubsAutoPayment } from "@/hooks/api/user.hooks";
import { useLang } from "@/hooks/useLang";
import useModalRouter from "@/hooks/useModalRouter";
import { cn } from "@/utils/cn.ts";
import { onError } from "@/utils/onError";
import { onSuccess } from "@/utils/onSuccess";

type Option = { id: number; value: string };

const CancelSubscriptionModal = (): JSX.Element => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Option | null>(null);

  const options: Option[] = [
    { id: 1, value: t("CancelReasons.1") },
    { id: 2, value: t("CancelReasons.2") },
    { id: 3, value: t("CancelReasons.3") },
    { id: 4, value: t("CancelReasons.4") },
    { id: 5, value: t("CancelReasons.5") },
  ];

  const isOther = JSON.stringify(selected) === JSON.stringify({ id: 0, value: "other" });

  const [params] = useSearchParams();

  const { closeModal } = useModalRouter();

  const subsId = params.get("subscription_id");

  const inputRef = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();

  const lang = useLang();

  const { mutate: changeAutoPayment, isPending } = useChangeSubsAutoPayment();

  useEffect(() => {
    if (isOther && inputRef.current) inputRef.current.focus();
  }, [isOther]);

  if (!subsId) return <></>;

  const handleChange = () => {
    if (!selected) {
      toast(t("take-option"), { type: "error", theme: "dark" });

      return;
    }

    if (isOther && !inputRef.current?.value.length) {
      toast(t("enter-field"), { type: "error", theme: "dark" });

      return;
    }

    changeAutoPayment(
      {
        subscription_id: +subsId,
        auto_payment: false,
        reason: isOther ? inputRef.current?.value : selected?.value,
      },
      {
        onSuccess: () => {
          onSuccess(t("Subscription.canceled"));
          queryClient.invalidateQueries({ queryKey: USER_KEYS.userDetail(lang) });
          closeModal();
        },
        onError,
      },
    );
  };

  return (
    <div className={"max-w-[37rem] space-y-6"}>
      <div>
        <div className={"staff-modal__header text-center"}>
          <DialogTitle as={"h2"} className={"staff-modal__title"}>
            {t("Subscription.reason-of-unsub")}
          </DialogTitle>
          <Description className={"staff-modal__description"}>{t("Subscription.reason-of-unsub-message")}</Description>
        </div>

        <RadioGroup
          value={selected}
          onChange={setSelected}
          aria-label="Server size"
          className={"mt-4 flex flex-col gap-3"}
        >
          {options.map((option) => (
            <Radio
              key={option.id}
              value={option}
              as={"div"}
              className={cn(
                "bg-surface-container relative flex cursor-pointer items-center justify-between gap-4 rounded-2xl px-4 py-5 text-xl/6 outline outline-1 -outline-offset-1 outline-transparent transition-all focus-visible:outline-white",
                "max-lg:p-4 max-lg:text-base/5",
              )}
            >
              <p>{option.value}</p>
              <IconRadio
                color={selected?.id === option.id ? COLORS["orange-1"] : COLORS["on-surface"]}
                active={selected?.id === option.id}
                className="size-6"
              />
            </Radio>
          ))}
          <Radio
            value={{ id: 0, value: "other" }}
            as={"div"}
            className={cn(
              "bg-surface-container focus-visible:outline-on-primary relative cursor-pointer space-y-2.5 rounded-2xl px-4 py-5 outline outline-1 -outline-offset-1 outline-transparent transition-all",
              "max-lg:p-4",
            )}
          >
            <div className={"flex items-center justify-between text-xl/6 max-lg:max-lg:text-base/5"}>
              <p>{t("Subscription.other")}</p>
              <IconRadio
                color={isOther ? COLORS["orange-1"] : COLORS["on-surface"]}
                active={isOther}
                className="size-6"
              />
            </div>
            <div className={cn({ hidden: !isOther })}>
              <Input type={"text"} ref={inputRef} placeholder={t("Subscription.tell-us-more")} />
            </div>
          </Radio>
        </RadioGroup>
      </div>
      <Button isLoading={isPending} aria-label={t("Subscription.cancel-subscription")} onClick={handleChange}>
        {t("Subscription.cancel-subscription")}
      </Button>
    </div>
  );
};

export default CancelSubscriptionModal;
